import { Button, Checkbox } from "@blueprintjs/core"
import classNames from "classnames"
import { TrafficCone } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useEffect, useRef, useState } from "react"
import { formatTrafficFetchedAt } from "./mapUtils"
import { RouteTrafficSettings, TrafficIncident } from "./routeTraffic"
import TrafficIncidentsDialog from "./TrafficIncidentsDialog"
import { IncidentOnRoute, IncidentTypeCount } from "./trafficIncidents"

type RouteTrafficSwitchProps = {
  settings: RouteTrafficSettings
  onSettingsChange: (settings: RouteTrafficSettings) => void
  /** Incident types on the current route */
  incidentTypes: IncidentTypeCount[]
  trafficFetchedAt?: string
  /** Every incident on the route with its km, for the "All incidents" list */
  incidents?: IncidentOnRoute[]
  onShowIncident?: (incident: TrafficIncident) => void
  /** Placement; the default is a fixed spot below the other buttons */
  className?: string
}

/** Map button for HERE traffic colors and incident icons (`route-traffic`). */
const RouteTrafficSwitch = ({
  settings,
  onSettingsChange,
  incidentTypes,
  trafficFetchedAt,
  incidents = [],
  onShowIncident,
  className = "absolute left-[10px] top-[185px] z-10",
}: RouteTrafficSwitchProps) => {
  const { t } = useTranslation()
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  // Outside the menu: the menu closes when the list opens
  const [isListOpen, setIsListOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isPanelOpen) {
      return
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsPanelOpen(false)
      }
    }

    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [isPanelOpen])

  const toggleType = (type: string) => {
    // Types not on this route stay ticked for the next route that has them
    onSettingsChange({
      ...settings,
      incidentTypes: settings.incidentTypes.includes(type)
        ? settings.incidentTypes.filter((ticked) => ticked !== type)
        : [...settings.incidentTypes, type],
    })
  }

  const isActive =
    settings.colors ||
    incidentTypes.some(({ type }) => settings.incidentTypes.includes(type))

  const trafficTime = formatTrafficFetchedAt(trafficFetchedAt)

  return (
    <div ref={containerRef} className={className}>
      <Button
        id="toggle-route-traffic"
        data-testid="toggle-route-traffic"
        active={isActive}
        className={classNames(
          "!h-[30px] !min-h-[30px] !w-[30px] !min-w-[30px] !p-0 dark:text-white",
          isActive
            ? "!bg-[#14ae5c] !text-white hover:!bg-[#129651] dark:!bg-[#14ae5c]"
            : "!text-[#5f6b7c] hover:!text-[#5f6b7c] dark:bg-gray-700 dark:!text-white",
        )}
        icon={<TrafficCone size={16} strokeWidth={2.25} />}
        onClick={() => setIsPanelOpen(!isPanelOpen)}
        title={t("maps.routeTraffic.title")}
      />
      {isPanelOpen && (
        <div className="absolute left-[36px] top-0 w-max rounded bg-white p-2 shadow-md dark:bg-gray-700 dark:text-white">
          <Checkbox
            className="!mb-1"
            checked={settings.colors}
            label={t("maps.routeTraffic.colors")}
            onChange={() =>
              onSettingsChange({ ...settings, colors: !settings.colors })
            }
          />
          <div className="mb-1 mt-2 text-[11px] uppercase text-gray-500 dark:text-gray-300">
            {t("maps.routeTraffic.incidents")}
          </div>
          {incidentTypes.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-300">
              {t("maps.routeTraffic.noIncidents")}
            </div>
          )}
          {incidentTypes.map(({ type, label, count }) => (
            <Checkbox
              key={type}
              className="!mb-1 last:!mb-0"
              checked={settings.incidentTypes.includes(type)}
              label={`${label} (${count})`}
              onChange={() => toggleType(type)}
            />
          ))}
          {incidents.length > 0 && (
            <Button
              minimal
              small
              className="!mt-1 !px-0 text-blue-600 dark:!text-blue-300"
              onClick={() => {
                setIsPanelOpen(false)
                setIsListOpen(true)
              }}
            >
              {t("maps.routeTraffic.allIncidents", { value: incidents.length })}
            </Button>
          )}
          {trafficTime && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-300">
              {t("maps.routeTraffic.trafficFrom", { time: trafficTime })}
            </div>
          )}
        </div>
      )}
      <TrafficIncidentsDialog
        open={isListOpen}
        onOpenChange={setIsListOpen}
        incidents={incidents}
        trafficFetchedAt={trafficFetchedAt}
        onSelect={(incident) => {
          setIsListOpen(false)
          onShowIncident?.(incident)
        }}
      />
    </div>
  )
}

export default RouteTrafficSwitch
